import axios from 'axios'
import { config } from '../../Configs/firebase.config'

const API_BASE_URL = 'https://edc-powerplantc7-rhvgtpasbq-as.a.run.app/'
const JWT_TOKEN_KEY = 'jwt_token'
const REFRESH_TOKEN_KEY = 'refresh_token'
const FIREBASE_SECURE_TOKEN_URL = `https://securetoken.googleapis.com/v1/token?key=${config.firebaseConfig.apiKey}`

interface LoginRequest {
	email: string
	pass: string
}

interface LoginResponse {
	statusCode: number
	message: string | string[]
	data?: {
		uid?: string
		idToken?: string
		accessToken?: string
		refreshToken?: string
		expiresIn?: string
		expiredOn?: string
		user?: any
	}
	accessToken?: string // Some APIs might return token directly
	idToken?: string // Some APIs might return idToken directly
}

interface RefreshTokenResponse {
	id_token?: string
	access_token?: string
	refresh_token?: string
	expires_in?: string
	user_id?: string
	project_id?: string
}

class AuthService {
	private refreshRequest: Promise<string | null> | null = null

	private extractAccessToken(
		responseData: LoginResponse | RefreshTokenResponse | string,
	): string | null {
		if (typeof responseData === 'string') {
			return responseData
		}

		if ('data' in responseData && responseData.data?.idToken) {
			return responseData.data.idToken
		}

		if ('data' in responseData && responseData.data?.accessToken) {
			return responseData.data.accessToken
		}

		if ('idToken' in responseData && responseData.idToken) {
			return responseData.idToken
		}

		if ('accessToken' in responseData && responseData.accessToken) {
			return responseData.accessToken
		}

		if ('id_token' in responseData && responseData.id_token) {
			return responseData.id_token
		}

		if ('access_token' in responseData && responseData.access_token) {
			return responseData.access_token
		}

		return null
	}

	private extractRefreshToken(responseData: LoginResponse | RefreshTokenResponse): string | null {
		if ('data' in responseData && responseData.data?.refreshToken) {
			return responseData.data.refreshToken
		}

		if ('refresh_token' in responseData && responseData.refresh_token) {
			return responseData.refresh_token
		}

		return null
	}

	/**
	 * Login with email and password
	 * @param email User email
	 * @param password User password
	 * @returns Access token and user data
	 */
	async login(email: string, password: string): Promise<LoginResponse> {
		try {
			const response = await axios.post<LoginResponse>(
				`${API_BASE_URL}/api/v1/admin/auth/login`,
				{
					email,
					pass: password,
				},
			)

			console.log('🔐 Full Login Response:', response)
			console.log('🔐 Response Data:', response.data)

			const accessToken = this.extractAccessToken(response.data)
			const refreshToken = this.extractRefreshToken(response.data)

			if (accessToken) {
				this.setToken(accessToken)
				if (refreshToken) {
					this.setRefreshToken(refreshToken)
				}
				console.log(
					'✅ JWT Token stored in localStorage:',
					accessToken.substring(0, 20) + '...',
				)
			} else {
				console.error('⚠️ No access token found in response:', response.data)
			}

			return response.data
		} catch (error: any) {
			console.error('❌ Login failed:', error?.response?.data || error.message)
			throw error
		}
	}

	/**
	 * Logout - clear token from localStorage
	 */
	logout(): void {
		localStorage.removeItem(JWT_TOKEN_KEY)
		localStorage.removeItem(REFRESH_TOKEN_KEY)
		console.log('🚪 User logged out, token cleared')
	}

	/**
	 * Get stored JWT token
	 */
	getToken(): string | null {
		return localStorage.getItem(JWT_TOKEN_KEY)
	}

	/**
	 * Set JWT token in localStorage
	 */
	setToken(token: string): void {
		localStorage.setItem(JWT_TOKEN_KEY, token)
	}

	/**
	 * Get stored refresh token
	 */
	getRefreshToken(): string | null {
		return localStorage.getItem(REFRESH_TOKEN_KEY)
	}

	/**
	 * Set refresh token in localStorage
	 */
	setRefreshToken(token: string): void {
		localStorage.setItem(REFRESH_TOKEN_KEY, token)
	}

	hasRefreshToken(): boolean {
		return !!this.getRefreshToken()
	}

	isTokenExpired(token: string): boolean {
		const payload = this.decodeToken(token)
		const currentTime = Date.now() / 1000

		return !!payload.exp && payload.exp < currentTime
	}

	/**
	 * Check if user is authenticated (has valid token)
	 */
	isAuthenticated(): boolean {
		const token = this.getToken()
		if (!token) {
			return this.hasRefreshToken()
		}

		try {
			if (this.isTokenExpired(token)) {
				console.log('⚠️ Token expired')
				return this.hasRefreshToken()
			}

			return true
		} catch (error) {
			console.error('❌ Invalid token:', error)
			return this.hasRefreshToken()
		}
	}

	async ensureValidToken(): Promise<string | null> {
		const token = this.getToken()

		if (token) {
			try {
				if (!this.isTokenExpired(token)) {
					return token
				}
			} catch (error) {
				console.error('❌ Invalid token during validation:', error)
			}
		}

		if (!this.hasRefreshToken()) {
			this.logout()
			return null
		}

		return this.refreshToken()
	}

	/**
	 * Decode JWT token (without verification)
	 */
	private decodeToken(token: string): any {
		try {
			const base64Url = token.split('.')[1]
			const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
			const jsonPayload = decodeURIComponent(
				atob(base64)
					.split('')
					.map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
					.join(''),
			)
			return JSON.parse(jsonPayload)
		} catch (error) {
			throw new Error('Invalid token format')
		}
	}

	/**
	 * Get user info from token
	 */
	getUserFromToken(): any {
		const token = this.getToken()
		if (!token) {
			return null
		}

		try {
			return this.decodeToken(token)
		} catch (error) {
			return null
		}
	}

	/**
	 * Refresh access token
	 */
	async refreshToken(): Promise<string | null> {
		const refreshToken = this.getRefreshToken()
		if (!refreshToken) {
			return null
		}

		if (this.refreshRequest) {
			return this.refreshRequest
		}

		this.refreshRequest = (async () => {
			try {
				const body = new URLSearchParams({
					grant_type: 'refresh_token',
					refresh_token: refreshToken,
				})

				const response = await axios.post<RefreshTokenResponse>(
					FIREBASE_SECURE_TOKEN_URL,
					body.toString(),
					{
						headers: {
							'Content-Type': 'application/x-www-form-urlencoded',
						},
					},
				)

				const newAccessToken = this.extractAccessToken(response.data)
				const newRefreshToken = this.extractRefreshToken(response.data)

				if (newAccessToken) {
					this.setToken(newAccessToken)
					if (newRefreshToken) {
						this.setRefreshToken(newRefreshToken)
					}
					return newAccessToken
				}

				this.logout()
				return null
			} catch (error) {
				console.error('❌ Refresh token failed:', error)
				this.logout()
				return null
			} finally {
				this.refreshRequest = null
			}
		})()

		return this.refreshRequest
	}
}

export const authService = new AuthService()
